import './number.field.scss';
import {
  Box,
  FormHelperTextProps,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import {
  KeyboardEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { NumericFormat, NumericFormatProps } from 'react-number-format';

import { toNumber } from '@converge/utils';

export interface INumberFieldProps
  extends Pick<NumericFormatProps, 'isAllowed' | 'onValueChange'> {
  name: string;
  value?: number | null | string;
  asValue?: boolean;
  max?: number;
  min?: number;
  onChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  isCurrency?: boolean;
  isPercentage?: boolean;
  maxLength?: number;
  isDisabled?: boolean;
  placeholder?: string;
  error?: boolean;
  hasChanged?: boolean;
  hasLabelBold?: boolean;
  helperText?: string;
  FormHelperTextProps?: FormHelperTextProps;
}

export const NumberField = forwardRef(
  (
    {
      onChange,
      value,
      max,
      min,
      isCurrency,
      isDisabled,
      placeholder,
      hasLabelBold,
      isPercentage,

      name,
      asValue = isDisabled,
      error = false,
      hasChanged,
      onBlur,
      helperText,
      ...props
    }: INumberFieldProps,
    ref
  ) => {
    // Display value - what user sees (e.g. 22 for 22%)
    const [displayValue, setDisplayValue] = useState<
      number | string | undefined | null
    >(value);

    // API value - what gets sent to parent (e.g. 0.22 for 22%)
    const [apiValue, setApiValue] = useState<number | null>(null);

    useEffect(() => {
      // When value comes from backend
      if (value != null) {
        const numValue = Number(value);
        if (isPercentage) {
          // Display as percentage (e.g. 0.22 -> 22)
          const newDisplayValue = numValue * 100;
          let updatedDisplayValue = newDisplayValue;

          // Check if number has more than 2 decimal places and starts with "00" after decimal
          if (newDisplayValue != null) {
            const decimalStr = newDisplayValue.toString().split('.')[1];
            if (
              decimalStr &&
              decimalStr.length > 2 &&
              decimalStr.startsWith('00')
            ) {
              updatedDisplayValue = Math.floor(newDisplayValue);
            }
          }
          setDisplayValue(updatedDisplayValue);
          setApiValue(numValue);
        } else {
          setDisplayValue(value);
          setApiValue(numValue);
        }
      } else {
        setDisplayValue(null);
        setApiValue(null);
      }
    }, [isPercentage, value]);
    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
      (event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        }
      },
      []
    );

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newDisplayValue = event.target.value;
      const parsedValue = toNumber(newDisplayValue);

      setDisplayValue(newDisplayValue);

      // Update API value
      if (parsedValue != null) {
        const newApiValue = isPercentage ? parsedValue / 100 : parsedValue;
        setApiValue(newApiValue);
        if (onChange) {
          onChange(newApiValue);
        }
      } else {
        setApiValue(null);
        if (onChange) {
          onChange(null);
        }
      }
    };

    const handleOnBlur = () => {
      if (onBlur && apiValue != null) {
        onBlur(apiValue);
      }
    };

    return (
      <>
        {hasLabelBold && <Stack className="label-is-exist">{name}</Stack>}

        <NumericFormat
          data-testid="number-field"
          name={name}
          getInputRef={ref}
          customInput={TextField}
          InputProps={{
            startAdornment: isCurrency ? (
              <InputAdornment position={'start'}>
                <Box
                  style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    color: isDisabled ? 'rgba(0, 0, 0, 0.4)' : 'black',
                  }}
                >
                  $
                </Box>
              </InputAdornment>
            ) : undefined,
            className: 'NumericFormat-text-field',
            sx: {
              backgroundColor: 'white',
              fontWeight: 600,
              fontSize: '14px',

              '& input::placeholder': {
                fontWeight: 600,
                fontSize: '14px', // Change placeholder color here
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)', // default border color
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: ' #28c0a7',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: ' #28c0a7',
              },
            },
            onKeyUp,
          }}
          size="small"
          thousandSeparator=","
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          max={max}
          min={min}
          disabled={isDisabled}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          value={displayValue}
          decimalScale={2}
          {...props}
        />
      </>
    );
  }
);
